import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { STORAGE_BASE, LECTURE_GRADIENTS, trunc } from '../utils/api';

export default function SingleResearchPage({ paper, papers }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  if (!paper) return null;

  const img  = paper.cover_image
    ? (paper.cover_image.startsWith('http') ? paper.cover_image : STORAGE_BASE + paper.cover_image)
    : '';
  const file = paper.file_url
    ? (paper.file_url.startsWith('http') ? paper.file_url : STORAGE_BASE + paper.file_url)
    : '';

  const idx    = (papers || []).findIndex(x => x.id === paper.id);
  const gradBg = LECTURE_GRADIENTS[idx >= 0 ? idx % LECTURE_GRADIENTS.length : 0];

  const backLabel   = lang === 'ar' ? '← العودة للأبحاث'        : lang === 'ur' ? '← تحقیقات پر واپس'           : '← Back to Research';
  const aboutLabel  = lang === 'ar' ? '📖 عن البحث'             : lang === 'ur' ? '📖 تحقیق کے بارے میں'        : '📖 About the Research';
  const othersLabel = lang === 'ar' ? '📚 أبحاث أخرى'           : lang === 'ur' ? '📚 دیگر تحقیقات'             : '📚 Other Research';
  const noFile      = lang === 'ar' ? 'ملف هذا البحث غير متاح للعرض حالياً'
                    : lang === 'ur' ? 'اس تحقیق کی فائل ابھی دستیاب نہیں ہے۔'
                    : 'This research file is not available for preview.';

  return (
    <div id="page-research-single">

      {/* ── Hero ── */}
      <div className="page-hero" style={{minHeight:200,padding:'1.2rem 0 1.2rem',background:gradBg}}>
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
              {t('nav.home.label')}
            </a>
            <span>/</span>
            <a href="/research" onClick={e => { e.preventDefault(); navigate('/research'); }}>
              {t('res.title1')}
            </a>
            <span>/</span>
            <span>{trunc(paper.title || '…', 50)}</span>
          </div>
          <h1 style={{ fontSize: 'clamp(1.2rem,3vw,1.8rem)', lineHeight: 1.5 }}>{paper.title}</h1>
          {paper.authors && (
            <p style={{ opacity: .8, fontSize: 'var(--t-sm)', marginTop: '.4rem' }}>✍️ {paper.authors}</p>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="pw" style={{ padding: '1.5rem 0 3rem' }}>
        <div className="lecture-viewer-grid">

          {/* ── Left: PDF viewer + About box ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>

            <div className="lecture-pdf-panel">
              <div className="lpdf-toolbar">
                <div className="lpdf-toolbar-left">
                  <span style={{ fontSize: '.85rem', fontFamily: 'var(--fam-display)', opacity: .85 }}>
                    📄 {t('res.title1')}
                  </span>
                </div>
                <div className="lpdf-toolbar-right">
                  {file && <>
                    <a href={file} target="_blank" rel="noreferrer" className="lpdf-btn">
                      {t('label.download.file')}
                    </a>
                    <a
                      href={file}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        background: 'rgba(255,255,255,.15)', color: '#fff', borderRadius: 7,
                        padding: '.3rem .75rem', fontSize: '.82rem', fontFamily: 'var(--fam-display)',
                        fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      {t('label.open.new.tab')}
                    </a>
                  </>}
                </div>
              </div>

              <div className="lpdf-canvas-wrap">
                {file
                  ? <iframe
                      src={`${file}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                      style={{ width: '100%', height: 780, border: 'none', borderRadius: 6, boxShadow: '0 4px 24px rgba(0,0,0,.12)', display: 'block' }}
                      title={paper.title}
                    />
                  : <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '.75rem' }}>📄</div>
                      <p style={{ color: 'var(--ink3)' }}>{noFile}</p>
                      <button className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/research')}>
                        {backLabel}
                      </button>
                    </div>
                }
              </div>
            </div>

            {/* About box */}
            <div className="single-sbox">
              <div className="ssb-head">{aboutLabel}</div>
              <div style={{ padding: '1.2rem' }}>
                <div style={{ fontFamily: 'var(--fam-display)', fontSize: 'var(--t-md)', color: 'var(--ink)', fontWeight: 700, marginBottom: '.6rem' }}>
                  {paper.title}
                </div>
                {paper.authors        && <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink3)', marginBottom: '.3rem' }}>✍️ {paper.authors}</div>}
                {paper.category       && <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink3)', marginBottom: '.3rem' }}>🏷 {paper.category}</div>}
                {paper.published_year && <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink3)', marginBottom: '.3rem' }}>📅 {paper.published_year}</div>}
                {paper.abstract       && <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink2)', lineHeight: 1.7, marginTop: '.6rem' }}>{paper.abstract}</div>}
                {file && (
                  <a href={file} target="_blank" rel="noreferrer" className="scf-btn"
                    style={{ display: 'inline-block', marginTop: '1rem', width: '100%', textAlign: 'center' }}>
                    {t('label.download.research')}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <aside className="lecture-viewer-aside">
            <div className="single-sbox">
              <div className="ssb-head">{othersLabel}</div>
              <div className="sbox-body">
                {(papers || []).filter(x => x.id !== paper.id).slice(0, 6).map(r => (
                  <div key={r.id} className="slist-item" onClick={() => navigate(`/research/${r.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="slist-n" style={{ background: 'var(--g1)', color: '#fff', borderRadius: 6, padding: '.15rem .45rem', fontSize: '.72rem', flexShrink: 0 }}>
                      📄
                    </div>
                    <a href={`/research/${r.id}`} onClick={e => e.preventDefault()}>
                      {trunc(r.title || '…', 55)}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="hadith-widget">
              <div className="geo-sm" />
              <div className="title">{t('lecsin.hadith2.title')}</div>
              <p>{t('lecsin.hadith2.text')}</p>
              <div className="ref">{t('lecsin.hadith2.ref')}</div>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}